/**
 * ClientDetailPage Component
 *
 * Feature: 126-client-assignment-views (T016)
 * Description: Full-page route component for Client Detail view with tabbed layout.
 *   Shows client summary header followed by tabs for Capacities, Workspaces, SLA.
 *   Uses useParams for clientId, useSearchParams for tab deep-linking.
 *   Lazy-loads each tab's data on first activation.
 * Constitution: v1.12.0 — Principle XXXVII (Code Headers), VII (Performance <2s)
 * Modified: 2026-06-15 | Spec 197 — conformed to the design blueprint: design
 *           tokens, kit Card + Tabs. The back link + title live in the existing
 *           <ClientSummaryHeader>. Presentation-only; zero behavior change.
 */

import React, { Suspense, useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Building2, Cpu, FolderOpen, Shield, Radar } from 'lucide-react';
import { clientApi } from '../../services/clientApi';
import { usePageData } from '../../hooks/usePageData';
import { ClientSummaryHeader } from '../../components/client/ClientSummaryHeader';
import { CapacityAssignmentsTab } from '../../components/client/CapacityAssignmentsTab';
import { WorkspaceAssignmentsTab } from '../../components/client/WorkspaceAssignmentsTab';
import { SlaAssignmentsTab } from '../../components/client/SlaAssignmentsTab';
import { BlastRadiusPanel } from '../../components/client/BlastRadiusPanel';
import { Badge, Card, DetailLayout, Tabs } from '../../components/ui';
import type { TabItem } from '../../components/ui';
import { type ClientDetailTab, isValidTab } from '@shared/types/client-detail';

/** GUID shape validation (Principle XXVII). NOT RFC-4122-strict: entity ids are
 * native Azure/Fabric/Org GUIDs (often NEWSEQUENTIALID) whose version/variant
 * nibbles aren't v4-compliant, so a strict v4 regex wrongly rejects most clients. */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Tab configuration */
interface TabConfig {
  id: ClientDetailTab;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  countKey: 'capacityCount' | 'workspaceCount' | null;
}

const TABS: TabConfig[] = [
  { id: 'capacities', label: 'Capacities', icon: Cpu, countKey: 'capacityCount' },
  { id: 'workspaces', label: 'Workspaces', icon: FolderOpen, countKey: 'workspaceCount' },
  { id: 'sla', label: 'SLA', icon: Shield, countKey: null },
  { id: 'blast-radius', label: 'Blast Radius', icon: Radar, countKey: null },
];

/** Loading spinner for tab content */
const TabLoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center py-12">
    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[var(--color-accent)]" />
  </div>
);

export const ClientDetailPage: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get current tab from URL or default to 'capacities'
  const tabParam = searchParams.get('tab');
  const currentTab: ClientDetailTab = isValidTab(tabParam) ? tabParam : 'capacities';

  // Track which tabs have been activated (for lazy loading)
  const [activatedTabs, setActivatedTabs] = useState<Set<ClientDetailTab>>(new Set([currentTab]));

  // Validate clientId format
  const isValidClientId = clientId && UUID_REGEX.test(clientId);

  // Fetch client detail
  const query = useQuery({
    queryKey: ['client', clientId, 'detail'],
    queryFn: () => clientApi.getClientById(clientId!),
    enabled: !!isValidClientId,
    staleTime: 30_000,
    retry: (failureCount, error: Error) => {
      // Don't retry on 404
      if (error.message.includes('not found') || error.message.includes('404')) {
        return false;
      }
      return failureCount < 2;
    },
  });
  const { data: client, isLoading, error, refresh, isRefreshing, lastFetchedAt } = usePageData(query);

  // Sync current tab to URL
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (!isValidTab(urlTab) && urlTab !== null) {
      // Invalid tab in URL, reset to default
      setSearchParams({ tab: 'capacities' }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle tab change
  const handleTabChange = useCallback(
    (tab: ClientDetailTab) => {
      setSearchParams({ tab }, { replace: true });
      setActivatedTabs((prev) => new Set([...prev, tab]));
    },
    [setSearchParams]
  );

  // Invalid client ID format
  if (!isValidClientId) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center text-[var(--color-status-danger)]">
        <AlertCircle className="mb-4 h-12 w-12" />
        <h2 className="mb-2 text-xl font-semibold">Invalid Client ID</h2>
        <p className="mb-4 text-[var(--color-text-secondary)]">
          The client ID format is invalid. Please check the URL.
        </p>
        <button
          onClick={() => navigate('/admin/clients')}
          className="text-[var(--color-accent)] hover:underline"
        >
          Return to Clients List
        </button>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-[var(--color-accent)]" />
      </div>
    );
  }

  // Error state (client not found). Once the client has loaded, a failed refresh keeps it on
  // screen and shows the Refresh badge instead (Spec 226 FR-011).
  if (!client) {
    const message = error instanceof Error ? error.message : '';
    const is404 = message.includes('not found') || message.includes('404');

    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center text-[var(--color-status-danger)]">
        <AlertCircle className="mb-4 h-12 w-12" />
        <h2 className="mb-2 text-xl font-semibold">
          {is404 ? 'Client Not Found' : 'Error Loading Client'}
        </h2>
        <p className="mb-4 text-[var(--color-text-secondary)]">
          {is404
            ? 'The requested client does not exist or has been deleted.'
            : message || 'An error occurred while loading client data.'}
        </p>
        <button
          onClick={() => navigate('/admin/clients')}
          className="text-[var(--color-accent)] hover:underline"
        >
          Return to Clients List
        </button>
      </div>
    );
  }

  const tabItems: TabItem[] = TABS.map((tab) => {
    const Icon = tab.icon;
    const count = tab.countKey ? client[tab.countKey] : null;
    return {
      key: tab.id,
      label: tab.label,
      icon: <Icon className="h-4 w-4" />,
      count: count != null ? count : undefined,
    };
  });

  return (
    <DetailLayout
      title={client.clientName}
      subtitle={<>Code: <span className="font-mono">{client.clientAccountId}</span></>}
      icon={<Building2 className="h-6 w-6" />}
      badges={<Badge variant={client.isActive ? 'success' : 'default'}>{client.isActive ? 'Active' : 'Inactive'}</Badge>}
      backTo="/admin/clients"
      backLabel="Back to Clients"
      helpId="auto.client-detail"
      onRefresh={refresh}
      isRefreshing={isRefreshing}
      lastFetchedAt={lastFetchedAt}
      refreshError={error}
    >
      {/* Counts, created date, SLA tier and contract value */}
      <ClientSummaryHeader client={client} />

      {/* Tabs */}
      <Card padding="none">
        <Tabs
          tabs={tabItems}
          value={currentTab}
          onChange={(key) => handleTabChange(key as ClientDetailTab)}
        />

        {/* Tab Content */}
        <div className="p-6">
          <Suspense fallback={<TabLoadingSpinner />}>
            {/* Capacities Tab */}
            {currentTab === 'capacities' && activatedTabs.has('capacities') && (
              <CapacityAssignmentsTab clientId={clientId!} />
            )}

            {/* Workspaces Tab */}
            {currentTab === 'workspaces' && activatedTabs.has('workspaces') && (
              <WorkspaceAssignmentsTab clientId={clientId!} />
            )}

            {/* SLA Tab */}
            {currentTab === 'sla' && activatedTabs.has('sla') && (
              <SlaAssignmentsTab clientId={clientId!} />
            )}

            {/* Blast Radius Tab (Spec 178 US7) */}
            {currentTab === 'blast-radius' && activatedTabs.has('blast-radius') && (
              <BlastRadiusPanel clientId={clientId!} />
            )}
          </Suspense>
        </div>
      </Card>
    </DetailLayout>
  );
};

export default ClientDetailPage;
